/**
 * The page's side of the service worker (issue #3): registration, the wait for control, and
 * the two messages the worker answers (re-read the state; report its build).
 */

export const WORKER_URL = "/sw.js";

export function workersSupported(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator && typeof caches !== "undefined";
}

let registration: Promise<ServiceWorkerRegistration | null> | undefined;

/** Register the worker once per page load; null where workers are unavailable (an insecure origin, an old browser). */
export function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  registration ??= (async () => {
    if (!workersSupported()) return null;
    try {
      return await navigator.serviceWorker.register(WORKER_URL, { scope: "/" });
    } catch (e) {
      console.warn("service worker registration failed", e);
      return null;
    }
  })();
  return registration;
}

/** Wait until a worker controls this page, or give up after `timeoutMs` (the first load is uncontrolled until the worker claims it). */
export async function waitForControl(timeoutMs = 3000): Promise<boolean> {
  if (!workersSupported()) return false;
  if (navigator.serviceWorker.controller) return true;
  const reg = await registerWorker();
  if (!reg) return false;
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      navigator.serviceWorker.removeEventListener("controllerchange", on);
      resolve(!!navigator.serviceWorker.controller);
    }, timeoutMs);
    const on = () => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("controllerchange", on);
      resolve(true);
    };
    navigator.serviceWorker.addEventListener("controllerchange", on);
  });
}

/** Post a message to the controlling worker and wait for its reply (a MessageChannel round trip), or time out. */
export async function askWorker<T = unknown>(message: Record<string, unknown>, timeoutMs = 2000): Promise<T | null> {
  const worker = workersSupported() ? navigator.serviceWorker.controller : null;
  if (!worker) return null;
  return new Promise<T | null>((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), timeoutMs);
    channel.port1.onmessage = (e) => {
      clearTimeout(timer);
      resolve(e.data as T);
    };
    worker.postMessage(message, [channel.port2]);
  });
}

/** Tell the worker the shared state changed, waiting until it has re-read it. */
export async function notifyStateChanged(): Promise<void> {
  await askWorker({ type: "state-changed" });
}

/**
 * Ask the browser for a newer worker script and wait until it is installed (its shell
 * precached) or found unchanged. Resolves without a result; the caller checks the shell cache.
 */
export async function updateWorker(timeoutMs = 60_000): Promise<void> {
  const reg = await registerWorker();
  if (!reg) return;
  try {
    await reg.update();
  } catch (e) {
    console.warn("service worker update check failed", e);
    return;
  }
  const installing = reg.installing;
  if (!installing) return;
  await new Promise<void>((resolve) => {
    const timer = setTimeout(done, timeoutMs);
    function done() {
      clearTimeout(timer);
      installing!.removeEventListener("statechange", on);
      resolve();
    }
    function on() {
      if (installing!.state !== "installing") done();
    }
    installing.addEventListener("statechange", on);
    if (installing.state !== "installing") done();
  });
}
